import { all, call, delay, takeEvery, takeLatest, select, put, debounce } from 'redux-saga/effects';
import { downloadBlobFromUrl, processAndDownloadZip } from '../../utils';

import {
  getFiles as getFilesApi,
  getInstructionals as getInstructionalsApi,
  deleteFile as deleteFileApi,
  uploadFile as uploadFileApi,
  updateFile as updateFileApi,
  downloadFileBlob as downloadFileBlobApi,
  downloadZippedFiles as downloadZippedFilesApi,
} from '../../services';
import {
  displayNotification,
  getTemporaryId,
  checkOnline,
  getNotification,
} from '../notifications';
import { doRequestTags, doCreateTag } from '../tags';
import { REFRESH_VALUES, setPollingActive, setPollingActiveDone } from '../application';
import {
  REFRESH_SESSIONS,
  REQUEST_FILES,
  REQUEST_INSTRUCTIONALS,
  DELETE_FILE,
  UPDATE_FILES,
  UPLOAD_FILES,
  DOWNLOAD_FILES,
  deleteFileSuccess,
  receiveFiles,
  refreshSessions,
  receiveInstructionals,
  clearUploaded,
  uploadFileStarted,
  uploadFileCompleted,
} from './actions';
import { getNewTags, getTagIds } from './helpers';
import { getUploading } from './selectors';
import { getActiveSite } from '../sites';

export function* doDeleteFile(action) {
  const { fileId } = action;
  const fileObject = yield select((state) => state.files.fileObjects[fileId]);

  if (!fileObject) return;

  try {
    yield call(deleteFileApi, fileId);
    yield put(deleteFileSuccess(fileId));
    yield put(displayNotification(getNotification('deleteFile', 'success')(fileObject.filename)));
    // TODO: Maybe remove call to refresh and hook into DELETE_FILE_SUCCESS to
    // remove the file in all active sessions? Downside is that pagination
    // will not be accurate.
    yield put(refreshSessions());
  } catch (error) {
    console.error('Unable to delete file:', error);
    yield call(checkOnline);
    yield put(
      displayNotification(getNotification('deleteFile', 'error')(fileObject.filename, fileId))
    );
  }
}

export function* doRequestFiles({ sessionId, query: originalQuery }) {
  const query = {
    ...originalQuery,
    page: originalQuery.page || 1,
    sortBy: originalQuery.sortBy || 'createdAt',
    order: originalQuery.order || 'desc',
  };
  if (!originalQuery.search) {
    delete query.search;
  }
  if (!originalQuery.fileType) {
    delete query.fileType;
  }

  try {
    const { values: receivedFiles, pages } = yield call(getFilesApi, query);
    yield put(receiveFiles(sessionId, receivedFiles, pages, query));
  } catch (error) {
    console.error('Unable to fetch files:', error);
    yield call(checkOnline);
    yield put(receiveFiles(sessionId, [], null, query));
    yield put(displayNotification(getNotification('getFiles', 'error')()));
  }
}

export function* doRequestInstructionals() {
  try {
    const { values: receivedFiles } = yield call(getInstructionalsApi);
    yield put(receiveInstructionals(receivedFiles));
  } catch (error) {
    console.error('Unable to fetch files:', error);
    yield call(checkOnline);
    yield put(receiveInstructionals([]));
    yield put(displayNotification(getNotification('getFiles', 'error')()));
  }
}

export function* doRefreshSessions() {
  yield put(setPollingActive('files'));

  const sessions = yield select((state) => state.files.sessions);

  yield all(
    Object.entries(sessions).map(([sessionId, { query }]) =>
      call(doRequestFiles, { sessionId, query })
    )
  );
  yield put(setPollingActiveDone('files'));
}

export function* doUploadFile(file) {
  const formData = new FormData();
  for (const key of Object.keys(file)) {
    if (Array.isArray(file[key])) {
      for (const item of file[key]) {
        formData.append(`${key}[]`, item);
      }
    } else {
      formData.append(key, file[key]);
    }
  }

  yield put(uploadFileStarted(file));
  const temporaryId = getTemporaryId();
  yield put(displayNotification(getNotification('createFile', 'pre')(file.file.name, temporaryId)));

  try {
    yield call(uploadFileApi, formData);

    yield put(uploadFileCompleted(file, 'SUCCESS'));
    yield put(
      displayNotification(getNotification('createFile', 'success')(file.file.name, temporaryId))
    );

    yield put(refreshSessions());
    yield delay(3000);
    const numberUploads = yield select(getUploading);
    if (numberUploads && numberUploads.length === 0) {
      yield put(clearUploaded());
    }
  } catch (error) {
    console.error('Unable to upload file:', error);
    yield call(checkOnline);
    yield put(uploadFileCompleted(file, 'ERROR'));
    yield put(
      displayNotification(getNotification('createFile', 'error')(file.file.name, temporaryId))
    );
  }
}

export function* doUploadFiles(action) {
  const { siteId, files, org } = action;
  try {
    const localTags = yield select((state) =>
      state.tags.tags.filter((t) => t.site === siteId && t.name === t.id)
    );
    const newUniqueTags = getNewTags(localTags, files);

    if (newUniqueTags.length) {
      yield all(
        newUniqueTags.map((tagName) =>
          call(doCreateTag, {
            org,
            site: siteId,
            name: tagName,
            categories: ['files'],
          })
        )
      );
      yield call(doRequestTags, { query: { site: siteId } });
    }

    const updatedTags = yield select((state) => state.tags.tags.filter((t) => t.site === siteId));

    yield all(
      files.map((file) => call(doUploadFile, { ...file, tags: getTagIds(updatedTags, file.tags) }))
    );
  } catch (error) {
    // individual tag creation / file upload failures are handled, this should catch
    // and display upload failure based on tag failure.
    console.error('Unable to upload files:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('filesCreateTag', 'error')('upload')));
  }
}

export function* doUpdateFile(file) {
  yield put(displayNotification(getNotification('updateFile', 'pre')(file.file.name, file.id)));

  try {
    yield call(updateFileApi, file.id, file);
    yield put(
      displayNotification(getNotification('updateFile', 'success')(file.file.name, file.id))
    );
    // TODO: This is in theory not necessary, we could just update the updated
    // file in all active sessions.
    yield put(refreshSessions());
  } catch (error) {
    console.error('Unable to update file:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateFile', 'error')(file.file.name, file.id)));
  }
}

export function* doUpdateFiles(action) {
  const { siteId, files, org } = action;

  try {
    const localTags = yield select((state) =>
      state.tags.tags.filter((t) => t.site === siteId && t.name === t.id)
    );
    const newUniqueTags = getNewTags(localTags, files);
    if (newUniqueTags.length) {
      yield all(
        newUniqueTags.map((tagName) =>
          call(doCreateTag, {
            org,
            site: siteId,
            name: tagName,
            categories: ['files'],
          })
        )
      );
      yield call(doRequestTags, { query: { site: siteId } });
    }

    const updatedTags = yield select((state) => state.tags.tags.filter((t) => t.site === siteId));
    yield all(
      files.map((file) => call(doUpdateFile, { ...file, tags: getTagIds(updatedTags, file.tags) }))
    );
  } catch (error) {
    // individual tag creation / file upload failures are handled, this should catch
    // and display upload failure based on tag failure.
    console.error('Unable to update files:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('filesCreateTag', 'error')('update')));
  }
}

// Helper to normalize input for doDownLoadFiles
function normalizeFilesInput(fileOrFiles) {
  if (Array.isArray(fileOrFiles)) {
    return fileOrFiles;
  }
  if (typeof fileOrFiles === 'object' && fileOrFiles !== null) {
    return [fileOrFiles];
  }
  return [];
}

function* doDownLoadFiles(action) {
  try {
    const files = normalizeFilesInput(action.fileOrFiles);
    const { filename } = action;
    const site = yield select(getActiveSite);

    if (files.length > 1) {
      // Zipped download
      const fileIds = files.map((f) => f.id);
      const blob = yield call(downloadZippedFilesApi, {
        file: fileIds,
        site: site?.id,
        org: site?.org,
      });
      const failedFiles = yield call(processAndDownloadZip, blob, filename || 'download.zip');

      failedFiles?.length > 0
        ? yield put(
            displayNotification(
              getNotification('getZippedFiles', 'partialSuccess')(
                failedFiles?.length,
                failedFiles
                  .map((nfId) => {
                    const file = files.find(({ id }) => id === nfId);
                    return file && file.filename;
                  })
                  .join(', ')
              )
            )
          )
        : yield put(
            displayNotification(getNotification('downloadFile', 'success')('Download complete'))
          );
    } else if (files.length === 1) {
      const file = files[0];
      const { id } = file;
      const fileName = file.filename || filename;
      const blob = yield call(downloadFileBlobApi, id);
      yield call(downloadBlobFromUrl, blob, fileName);
      yield put(
        displayNotification(getNotification('downloadFile', 'success')('Download complete'))
      );
    }
  } catch (error) {
    console.error('Error downloading file:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('downloadFile', 'error')()));
  }
}

export const sagas = [
  takeEvery(REQUEST_FILES, doRequestFiles),
  takeEvery(REQUEST_INSTRUCTIONALS, doRequestInstructionals),
  takeEvery(DELETE_FILE, doDeleteFile),
  takeLatest(UPLOAD_FILES, doUploadFiles),
  takeLatest(UPDATE_FILES, doUpdateFiles),
  takeEvery(DOWNLOAD_FILES, doDownLoadFiles),
  takeLatest(REFRESH_SESSIONS, doRefreshSessions),
  debounce(500, REFRESH_VALUES, doRefreshSessions),
];
